import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { DashboardLayout } from '../components/layout/DashboardSidebar';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const PerformancePage = () => {
  const [stats, setStats]     = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/users/dashboard')
      .then(res => {
        if (res.data.success) {
          setStats(res.data.data.quizStats || {});
          setHistory(res.data.data.recentQuizzes || []);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Safe accessors — prevents "can't access property of null"
  const totalQuizzes = stats?.totalQuizzes || 0;
  const totalCorrect = stats?.totalCorrect || 0;
  const totalWrong   = stats?.totalWrong   || 0;
  const avgScore     = Math.round(stats?.avgScore || 0);
  const skipped      = Math.max(0, (totalQuizzes * 10) - totalCorrect - totalWrong);

  const pieData = {
    labels: ['Correct', 'Wrong', 'Skipped'],
    datasets: [{
      data: [totalCorrect, totalWrong, skipped],
      backgroundColor: ['#4ade80', '#f87171', '#94a3b8'],
      borderColor: ['#22c55e', '#ef4444', '#64748b'],
    }],
  };

  return (
    <DashboardLayout title="Performance">
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Performance Analytics</h1>

        {loading ? (
          <div className="text-center py-20 text-text-muted animate-pulse">Loading Analysis...</div>
        ) : (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard label="Tests Taken"  value={totalQuizzes}  icon="📝" />
              <StatCard label="Avg Score"    value={avgScore}      unit="%" icon="🎯" />
              <StatCard label="Total Correct" value={totalCorrect} icon="✅" color="text-green-400" />
              <StatCard label="Total Wrong"  value={totalWrong}    icon="❌" color="text-red-400" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Pie Chart */}
              <div className="md:col-span-1 bg-white/5 p-6 rounded-2xl border border-white/10">
                <h3 className="font-bold mb-4">Accuracy Distribution</h3>
                {totalQuizzes > 0 ? (
                  <div className="h-64 flex justify-center">
                    <Pie data={pieData} options={{ responsive: true, plugins: { legend: { position: 'bottom', labels: { color: 'white' } } } }} />
                  </div>
                ) : (
                  <div className="text-text-muted text-center py-12">No data yet. Take some tests to see your analysis.</div>
                )}
              </div>

              {/* History Table */}
              <div className="md:col-span-2 bg-white/5 p-6 rounded-2xl border border-white/10">
                <h3 className="font-bold mb-4">Recent Test History</h3>
                {history.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-white/10 text-accent-gold">
                        <tr>
                          <th className="p-3 rounded-tl-lg">Date</th>
                          <th className="p-3">Test Name</th>
                          <th className="p-3">Score</th>
                          <th className="p-3">Status</th>
                          <th className="p-3 rounded-tr-lg">Review</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10">
                        {history.map((h, i) => (
                          <tr key={i} className="hover:bg-white/5">
                            <td className="p-3">{new Date(h.SubmittedDate).toLocaleDateString('en-IN', { timeZone: 'UTC' })}</td>
                            <td className="p-3">{h.Title}</td>
                            <td className="p-3 font-bold">{h.MarksObtained}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded text-xs ${h.Status === 'Passed' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                                {h.Status}
                              </span>
                            </td>
                            <td className="p-3">
                              <Link to={`/test-review/${h.QuizId}`} className="text-xs text-accent-gold hover:underline">
                                View Solutions →
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-text-muted text-center py-12">No test history yet.</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

const StatCard = ({ label, value, unit = '', icon, color = 'text-white' }) => (
  <div className="bg-white/5 p-4 rounded-xl border border-white/10 backdrop-blur-sm">
    <div className="flex justify-between items-start">
      <div><p className="text-text-muted text-sm">{label}</p><h4 className={`text-2xl font-bold mt-1 ${color}`}>{value}{unit}</h4></div>
      <span className="text-2xl opacity-50">{icon}</span>
    </div>
  </div>
);

export default PerformancePage;
